import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Storefront, Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const BG = "https://images.unsplash.com/photo-1712629390041-9cd2a94b2fb3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxzaG9lJTIwc3RvcmUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODkzMzY4MjB8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, error, user } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(username, password);
    setLoading(false);
    if (ok) navigate("/");
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <div className="flex w-full flex-col justify-center px-6 py-12 lg:w-1/2 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-700 text-white">
              <Storefront size={24} weight="bold" />
            </div>
            <div>
              <div className="font-display text-xl font-extrabold tracking-tight text-slate-900">S.S GROUP</div>
              <div className="text-xs text-slate-500">Retail Sales Analytics</div>
            </div>
          </div>

          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Welcome back</h1>
          <p className="mt-2 text-sm text-slate-500">Sign in to view your store performance.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Username</label>
              <input
                data-testid="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-700"
                placeholder="e.g. admin"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Password</label>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full rounded-md border border-slate-300 px-3 py-2.5 pr-10 text-sm outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-700"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full rounded-md bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-800 disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>

      <div className="relative hidden lg:block lg:w-1/2">
        <img src={BG} alt="Shoe store interior" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/50" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="font-display text-4xl font-extrabold leading-tight tracking-tight">
            How are we doing today?
          </div>
          <p className="mt-3 max-w-md text-slate-200">
            Upload your daily POS export and instantly see ABV, ABQ and performance across every floor and salesman.
          </p>
        </div>
      </div>
    </div>
  );
}
